import type { LegalSourceToolName } from "~/app/lib/source-suggestions";

export const LEGAL_SOURCE_TOOLS_STORAGE_KEY =
  "modelSettings.legalSourceEnabledTools";

export const DEFAULT_LEGAL_SOURCE_TOOLS = [
  "Polish_Law_MCP_Agent",
  "Search_Eureka",
  "Search_SN",
  "Search_SP",
  "Search_TK",
  "Search_NSA",
  "Search_KIO",
  "EU_Regulations_MCP",
  "Search_UOKIK",
  "Search_UOKIK_Decisions",
  "Search_UODO",
  "Search_KNF",
  "Search_BZP",
  "Get_Current_KRS_Record",
  "Get_Full_KRS_Record",
] as const satisfies readonly LegalSourceToolName[];

export const DEFAULT_LEGAL_SOURCE_TOOL_SET = new Set<string>(
  DEFAULT_LEGAL_SOURCE_TOOLS
);

export type LegalSourceToolOption = {
  toolName: LegalSourceToolName;
  labelKey:
    | "LegalSource.SEJM"
    | "LegalSource.Eureka"
    | "LegalSource.SN"
    | "LegalSource.SP"
    | "LegalSource.TK"
    | "LegalSource.NSA"
    | "LegalSource.KIO"
    | "LegalSource.EURegulations"
    | "LegalSource.UOKIK"
    | "LegalSource.UOKIKDecisions"
    | "LegalSource.UODO"
    | "LegalSource.KNF"
    | "LegalSource.BZP"
    | "LegalSource.KRSCurrent"
    | "LegalSource.KRSFull";
  descriptionKey:
    | "LegalSource.SEJM.description"
    | "LegalSource.Eureka.description"
    | "LegalSource.SN.description"
    | "LegalSource.SP.description"
    | "LegalSource.TK.description"
    | "LegalSource.NSA.description"
    | "LegalSource.KIO.description"
    | "LegalSource.EURegulations.description"
    | "LegalSource.UOKIK.description"
    | "LegalSource.UOKIKDecisions.description"
    | "LegalSource.UODO.description"
    | "LegalSource.KNF.description"
    | "LegalSource.BZP.description"
    | "LegalSource.KRSCurrent.description"
    | "LegalSource.KRSFull.description";
};

export const LEGAL_SOURCE_TOOL_OPTIONS = [
  {
    toolName: "Polish_Law_MCP_Agent",
    labelKey: "LegalSource.SEJM",
    descriptionKey: "LegalSource.SEJM.description",
  },
  {
    toolName: "Search_Eureka",
    labelKey: "LegalSource.Eureka",
    descriptionKey: "LegalSource.Eureka.description",
  },
  {
    toolName: "Search_UOKIK",
    labelKey: "LegalSource.UOKIK",
    descriptionKey: "LegalSource.UOKIK.description",
  },
  {
    toolName: "Search_UOKIK_Decisions",
    labelKey: "LegalSource.UOKIKDecisions",
    descriptionKey: "LegalSource.UOKIKDecisions.description",
  },
  {
    toolName: "Search_UODO",
    labelKey: "LegalSource.UODO",
    descriptionKey: "LegalSource.UODO.description",
  },
  {
    toolName: "Search_KNF",
    labelKey: "LegalSource.KNF",
    descriptionKey: "LegalSource.KNF.description",
  },
  {
    toolName: "Search_SN",
    labelKey: "LegalSource.SN",
    descriptionKey: "LegalSource.SN.description",
  },
  {
    toolName: "Search_SP",
    labelKey: "LegalSource.SP",
    descriptionKey: "LegalSource.SP.description",
  },
  {
    toolName: "Search_TK",
    labelKey: "LegalSource.TK",
    descriptionKey: "LegalSource.TK.description",
  },
  {
    toolName: "Search_NSA",
    labelKey: "LegalSource.NSA",
    descriptionKey: "LegalSource.NSA.description",
  },
  {
    toolName: "Search_KIO",
    labelKey: "LegalSource.KIO",
    descriptionKey: "LegalSource.KIO.description",
  },
  {
    toolName: "EU_Regulations_MCP",
    labelKey: "LegalSource.EURegulations",
    descriptionKey: "LegalSource.EURegulations.description",
  },
  {
    toolName: "Search_BZP",
    labelKey: "LegalSource.BZP",
    descriptionKey: "LegalSource.BZP.description",
  },
  {
    toolName: "Get_Current_KRS_Record",
    labelKey: "LegalSource.KRSCurrent",
    descriptionKey: "LegalSource.KRSCurrent.description",
  },
  {
    toolName: "Get_Full_KRS_Record",
    labelKey: "LegalSource.KRSFull",
    descriptionKey: "LegalSource.KRSFull.description",
  },
] as const satisfies readonly LegalSourceToolOption[];

export const LEGAL_SOURCE_TOOL_NAME_SET = new Set<string>(
  LEGAL_SOURCE_TOOL_OPTIONS.map((option) => option.toolName)
);

export function sanitizeLegalSourceTools(
  value: unknown
): LegalSourceToolName[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (tool): tool is LegalSourceToolName =>
      typeof tool === "string" && DEFAULT_LEGAL_SOURCE_TOOL_SET.has(tool)
  );
}

/**
 * Whether a legal-source tool is currently selectable, given the BZP/KRS feature
 * flags (the rest are always available). Shared by the legacy and useChat chat
 * clients so they can't drift on which tools are offered.
 */
export function isLegalSourceToolAvailable(
  toolName: LegalSourceToolName,
  flags: { bzp: boolean; krs: boolean }
): boolean {
  switch (toolName) {
    case "Search_BZP":
      return flags.bzp;
    case "Get_Current_KRS_Record":
    case "Get_Full_KRS_Record":
      return flags.krs;
    case "Polish_Law_MCP_Agent":
    case "Search_Eureka":
    case "Search_SN":
    case "Search_SP":
    case "Search_TK":
    case "Search_NSA":
    case "Search_KIO":
    case "EU_Regulations_MCP":
    case "Search_UOKIK":
    case "Search_UOKIK_Decisions":
    case "Search_UODO":
    case "Search_KNF":
      return true;
    default:
      return false;
  }
}
